/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, April 2020
 */

import React, { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  styled,
  Box,
  Text,
  Theme,
  convertCssUnit,
  convertShorthand,
  Platform,
} from '@staffino/primitive-controls';
import StarButton from '../components/star-button';
import SettingsContext from '../settings-context';
import lerpScoreColors from './lerp-score-colors';

const colorPalette = ['#fc5b02', '#fea501', '#ffd800', '#66c51c', '#00b92e'];

type LabelTextProps = {
  theme?: Theme;
};

const LabelText = styled(Text)<LabelTextProps>((props) => ({
  textAlign: 'center',
  fontWeight: 'bold',
  fontSize: convertCssUnit('0.875rem'),
  wordBreak: 'normal',
  minWidth: convertCssUnit('89px'),
  position: 'absolute',
  left: '50%',
  marginLeft: -45,
  [props.theme.mq.small]: {
    marginLeft: 0,
    transform: 'translate(-50%)',
  },
  [props.theme.mq.large]: {
    whiteSpace: 'nowrap',
  },
}));

type Props = {
  name: string;
  lowerBoundLabel?: string;
  upperBoundLabel?: string;
  lowerBoundScore?: number;
  upperBoundScore?: number;
  highlighted?: boolean;
  colorStyle?: string;
  value?: number;
  onChange?: (score: number) => void;
  onSelect?: (score: number) => void;
} & React.ComponentProps<typeof Box>;

const StarsControl: React.ForwardRefExoticComponent<
  Props & React.RefAttributes<unknown>
> = React.forwardRef(
  (
    {
      name,
      lowerBoundLabel,
      upperBoundLabel,
      lowerBoundScore = 1,
      upperBoundScore = 5,
      highlighted,
      colorStyle,
      value,
      onChange,
      onSelect,
      ...rest
    }: Props,
    ref: React.Ref<unknown>
  ) => {
    const { t } = useTranslation();
    const { contentWidth } = useContext(SettingsContext);
    const stars = useMemo(
      // we are using reverse here because we want to have biggest score first,
      // so we can revert the stars using dir="rtl"
      () =>
        lerpScoreColors(
          lowerBoundScore,
          upperBoundScore,
          colorPalette
        ).reverse(),
      [lowerBoundScore, upperBoundScore]
    );

    const handleChange = useCallback(
      (starScore) => {
        const parsedScore = parseInt(starScore, 10);
        onChange?.(parsedScore);
        onSelect?.(parsedScore);
      },
      [onChange, onSelect]
    );
    const starCount = upperBoundScore - lowerBoundScore + 1;
    if (starCount <= 0) {
      return null;
    }
    const starWidth = Platform.select<number | string>({
      native: (contentWidth * 0.8) / starCount,
      default: `${Math.floor(10000 / starCount) / 100}%`,
    });
    const boxWidth = Platform.select<number | string>({
      default: '80%',
      native: contentWidth * 0.8,
    });

    return (
      <Box {...rest}>
        <Box
          flexDirection={'row-reverse' as const}
          justifyContent="center"
          // dir="rtl"
          width={boxWidth}
          alignSelf="center"
        >
          {stars.map((star) => (
            <StarButton
              key={star.color}
              color={colorStyle || star.color}
              score={star.score}
              width={starWidth}
              highlighted={highlighted || star.score <= value}
              name={name}
              innerRef={ref}
              onChange={handleChange}
            />
          ))}
        </Box>
        <Box
          flexDirection={'row' as const}
          width={boxWidth}
          {...convertShorthand('margin', '0.51rem auto 1.625rem')}
          // there is a problem with pixels between 0 and 8 (including),
          // e.g. 8 (0.5rem) converts to 512, don't know why.
          // so I changed 0.5rem to 0.51rem which leaves as is
        >
          <Box flexGrow={1} flexShrink={0}>
            <LabelText color="lightText">
              {lowerBoundLabel || t('Poor')}
            </LabelText>
          </Box>
          <Box flexGrow={starCount - 2} flexShrink={0} />
          <Box flexGrow={1} flexShrink={0}>
            <LabelText color="lightText">
              {upperBoundLabel || t('Excellent')}
            </LabelText>
          </Box>
        </Box>
      </Box>
    );
  },
);

export default StarsControl;
